import React, { useState, useEffect, useMemo } from 'react';
import { Box, Alert, Divider, useMediaQuery, useTheme, Button, IconButton } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ShareIcon from '@mui/icons-material/Share';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { useParams } from 'react-router-dom';

import { useGetCourseByIdQuery, useUpdateCourseMutation } from '../my-courses/coursesApi';
import { useGetUnitsByCourseIdQuery } from '../units/unitsApi';
import { useAppSelector, useAppDispatch } from '../../shared/hooks/redux';
import { useObjectPermissionState } from '../../shared/hooks/useCanEdit';
import { setIsMobile, setScopedAiContext, toggleScopedAiPanel } from '../../shared/store/uiSlice';
import { PageSpinner } from '../../shared/components/PageSpinner';
import AccessDeniedOrNotFound from '../../shared/components/AccessDeniedOrNotFound';
import ShareDialog from '../../shared/components/ShareDialog';
import { EditableText } from '../../shared/components/EditableText';

import { CourseOverviewCard } from './components/CourseOverviewCard';

import type { AgentContext } from '@/shared/types/chat';
import type { StoredStandard } from '@/shared/types/customStandard';
import type {
  CreateResourceRequest,
  GenerateFromTemplateRequest,
  Resource,
  ResourceParentLocator,
} from '@/shared/types/resource';
import {
  stripStorageImagePathsFromDocumentContent,
  stripStorageImagePathsFromSlideContent,
} from '@/shared/types/resource';
import { useSetPublicCascadeMutation } from '@/features/permissions/permissionsApi';
import StandardsCoverageMatrix from '@/shared/components/StandardsCoverageMatrix';
import { exportCurriculumPdf } from '@/shared/services/pdfExport';
import ResourceListCard from '@/shared/components/ResourceListCard';
import {
  useCreateFromTemplateMutation,
  useCreateResourceMutation,
  useDeleteResourceMutation,
  useGetResourcesQuery,
  useGetResourceTemplatesQuery,
  useUpdateResourceMutation,
  useGenerateFromTemplateMutation,
} from '@/features/resources/resourcesApi';
import { useGoogleDocsExport } from '@/features/resources/hooks/useGoogleDocsExport';
import { isFeatureEnabled } from '@/shared/libs/featureFlags';
import { ScoperAiDialog } from '@/features/chatbot/scoper-dev';

const CoursePage: React.FC = () => {
  const pdfExportEnabled = isFeatureEnabled('curriculumPdfExport');
  const scoperEnabled = import.meta.env.DEV && isFeatureEnabled('scoperAiDevDialog');
  const [scoperOpen, setScoperOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useAppDispatch();
  const { courseId } = useParams();
  const {
    data: course,
    error: courseError,
    isLoading: courseLoading,
  } = useGetCourseByIdQuery(courseId!);
  const scoperContext = useMemo<AgentContext | null>(
    () =>
      courseId
        ? {
            assistantType: 'course',
            courseId,
            focus: {
              level: 'course',
              courseId,
            },
          }
        : null,
    [courseId]
  );
  const { data: units = [] } = useGetUnitsByCourseIdQuery(courseId!, {
    skip: !courseId,
  });
  const { user } = useAppSelector((state) => state.auth);
  const isOwner = Boolean(course && user && course.owner_id === user.id);
  const coursePermission = useObjectPermissionState('course', courseId, 'editor', {
    fallbackAllowed: isOwner,
  });
  const canEdit = coursePermission.isAllowed;
  const [setPublicCascade] = useSetPublicCascadeMutation();
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const courseLocator: ResourceParentLocator = {
    parentCourseId: courseId!,
    courseId: courseId!,
  };
  const { data: resources = [] } = useGetResourcesQuery(courseLocator, { skip: !courseId });
  const { data: templates = [] } = useGetResourceTemplatesQuery({ templateKind: 'document' });
  const [createResource] = useCreateResourceMutation();
  const [updateResource] = useUpdateResourceMutation();
  const [deleteResource] = useDeleteResourceMutation();
  const [createFromTemplate] = useCreateFromTemplateMutation();
  const [generateFromTemplate] = useGenerateFromTemplateMutation();
  const [updateCourse, { isLoading: isUpdatingCourse }] = useUpdateCourseMutation();
  const { exportToGoogle } = useGoogleDocsExport({ onError: setExportError });

  // Compute standards coverage across units
  const unitColumns = useMemo(
    () =>
      (units ?? [])
        .slice()
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map((u) => ({ id: u.id, label: u.title })),
    [units]
  );

  const getUnitStandards = (u: any): StoredStandard[] =>
    (u?.content?.overview ?? [])
      .filter((it: any) => it?.type === 'standards')
      .flatMap((it: any) => it?.standards ?? []) as StoredStandard[];

  const courseStandardRows = useMemo(() => {
    const map = new Map<string, StoredStandard>();
    (units ?? []).forEach((u) => getUnitStandards(u).forEach((s) => map.set(s.id, s)));
    return Array.from(map.values());
  }, [units]);

  const coverageByUnit = useMemo(() => {
    const map: Record<string, StoredStandard[]> = {};
    (units ?? []).forEach((u) => {
      map[u.id] = getUnitStandards(u);
    });
    return map;
  }, [units]);

  // Update global UI state for mobile
  useEffect(() => {
    dispatch(setIsMobile(isMobile));
  }, [isMobile, dispatch]);

  useEffect(() => {
    if (!course) return;
    dispatch(
      setScopedAiContext({
        level: 'course',
        courseId: course.id,
        resourceId: undefined,
        resourceKind: undefined,
      })
    );
  }, [dispatch, course]);

  // Handler functions for updating course
  const handleUpdateTitle = async (newTitle: string) => {
    if (!course) return;

    await updateCourse({
      id: course.id,
      title: newTitle,
    }).unwrap();
  };

  const handleUpdateDescription = async (newDescription: string) => {
    if (!course) return;

    await updateCourse({
      id: course.id,
      description: newDescription,
    }).unwrap();
  };

  const handleExportPdf = async () => {
    if (!course) return;
    setExportError(null);
    setIsExportingPdf(true);
    try {
      await exportCurriculumPdf(
        'course',
        course.id,
        `${course.title || 'course'}-course-export.pdf`
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to export PDF';
      setExportError(message);
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleCreateResource = async (resourceData: CreateResourceRequest) => {
    if (!course) return;
    return createResource({
      ...courseLocator,
      resource: resourceData,
    }).unwrap();
  };

  const handleUpdateResource = async (resourceData: Resource) => {
    await updateResource({
      id: resourceData.id,
      title: resourceData.title,
      description: resourceData.description,
      content: resourceData.content,
      version: (resourceData.version ?? 1) + 1,
    }).unwrap();
  };

  const handleDeleteResource = async (resourceId: string) => {
    if (!course) return;
    await deleteResource({
      ...courseLocator,
      resourceId,
    }).unwrap();
  };

  const handleCreateFromTemplate = async (templateId: string) => {
    if (!course) return;
    return createFromTemplate({
      ...courseLocator,
      templateId,
    }).unwrap();
  };

  const handleGenerateFromTemplate = async (request: GenerateFromTemplateRequest) => {
    return generateFromTemplate(request).unwrap();
  };

  const handleDuplicateResource = async (resourceData: Resource) => {
    if (!course || resourceData.resourceKind === 'file') return;
    const clonedContent = JSON.parse(JSON.stringify(resourceData.content ?? {}));
    const duplicateContent =
      resourceData.resourceKind === 'document'
        ? stripStorageImagePathsFromDocumentContent(clonedContent)
        : resourceData.resourceKind === 'slide'
          ? stripStorageImagePathsFromSlideContent(clonedContent)
          : clonedContent;

    return createResource({
      ...courseLocator,
      resource: {
        resourceKind: resourceData.resourceKind,
        title: `Copy of ${resourceData.title}`,
        description: resourceData.description,
        content: duplicateContent,
      },
    }).unwrap();
  };

  if (courseLoading) {
    return <PageSpinner />;
  }

  if (courseError || !course) {
    return <AccessDeniedOrNotFound objectType="course" objectId={courseId} user={user} />;
  }

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 4 } }}>
      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {exportError}
        </Alert>
      )}

      {/* Header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          columnGap: 2,
          rowGap: 1,
          alignItems: 'start',
          mb: 3,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <EditableText
            value={course?.title ?? ''}
            onSave={handleUpdateTitle}
            canEdit={canEdit}
            isLoading={isUpdatingCourse}
            typographyProps={{
              variant: 'h2',
              component: 'h1',
            }}
            textFieldProps={{
              sx: {
                '& .MuiInputBase-input': {
                  fontSize: '2.125rem',
                  fontWeight: 300,
                  lineHeight: 1.235,
                },
              },
            }}
          />
        </Box>
        {((!isMobile && user) || canEdit) && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            {!isMobile && user && pdfExportEnabled && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<PictureAsPdfOutlinedIcon />}
                onClick={handleExportPdf}
                disabled={isExportingPdf}
              >
                {isExportingPdf ? 'Exporting...' : 'Export PDF'}
              </Button>
            )}
            {canEdit &&
              (isMobile ? (
                <>
                  <IconButton size="small" onClick={() => setShareOpen(true)} title="Share">
                    <ShareIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => dispatch(toggleScopedAiPanel())}
                    title="AI CoPlanner"
                  >
                    <AutoAwesomeIcon />
                  </IconButton>
                </>
              ) : (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ShareIcon />}
                    onClick={() => setShareOpen(true)}
                  >
                    Share
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={() => dispatch(toggleScopedAiPanel())}
                  >
                    AI CoPlanner
                  </Button>
                  {scoperEnabled && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={() => setScoperOpen(true)}
                      sx={{ borderStyle: 'dashed' }}
                    >
                      Dev AI
                    </Button>
                  )}
                </>
              ))}
          </Box>
        )}
        <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
          <EditableText
            value={course?.description ?? ''}
            onSave={handleUpdateDescription}
            canEdit={canEdit}
            isLoading={isUpdatingCourse}
            allowEmpty
            multiline
            typographyProps={{
              variant: 'subtitle1',
              color: 'text.secondary',
            }}
          />
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Overview */}
      {course && (
        <Box sx={{ mb: 4 }}>
          <CourseOverviewCard course={course} canEdit={canEdit} />
        </Box>
      )}

      {/* Resources */}
      {course && (
        <Box sx={{ mb: 4 }}>
          <ResourceListCard
            locator={courseLocator}
            resources={resources}
            templates={templates}
            onCreateResource={handleCreateResource}
            onCreateFromTemplate={handleCreateFromTemplate}
            onGenerateFromTemplate={handleGenerateFromTemplate}
            onUpdateResource={handleUpdateResource}
            onDelete={handleDeleteResource}
            onDuplicateResource={handleDuplicateResource}
            onExportToGoogle={exportToGoogle}
            canEdit={canEdit}
            title="Course Resources"
          />
        </Box>
      )}

      {/* Standards Coverage */}
      {course && (
        <Box sx={{ mb: 4 }}>
          <StandardsCoverageMatrix
            rows={courseStandardRows}
            columns={unitColumns}
            coverageByColumn={coverageByUnit}
            title="Standards Coverage Across Units"
            subtitle="Which units include each standard"
            dense
          />
        </Box>
      )}
      {course && (
        <ShareDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          objectId={course.id}
          objectType="course"
          ownerId={course.owner_id}
          isPublic={course.is_public}
          onTogglePublic={(val) =>
            setPublicCascade({
              object_type: 'course',
              object_id: course.id,
              is_public: val,
            }).unwrap()
          }
          shareUrl={`${window.location.origin}/curriculum/courses/${course.id}`}
        />
      )}
      {scoperEnabled && course && (
        <ScoperAiDialog
          open={scoperOpen}
          onClose={() => setScoperOpen(false)}
          context={scoperContext}
        />
      )}
    </Box>
  );
};
export default CoursePage;
